import { api, postNow } from './api'
import { getMe } from './me'
import { el, todayISO } from './ui'
import { lineDrawing } from './picture-tiles'
import { dayFigure, dayState, DAY_STATE_LABELS, type DayState, type DayStateOptions } from './day-state'
import type { Clean } from './types'
import './diary-colours.css'

export function stateMark(state: DayState): HTMLElement {
  return el('span', { class: 'diary-state-mark', 'aria-hidden': 'true' },
    state === 'done' ? lineDrawing('tick', 13) : state === 'pencilled' ? lineDrawing('pencil', 13) : state === 'agreed' ? '•' : '+')
}

let context = ''
const seen = new Map<string, ReturnType<typeof dayState>>()
export function beginDiaryColours(view: string, filters: string): void {
  const me = getMe(), next = JSON.stringify([me?.businessId, me?.email, view, filters])
  if (next !== context) { seen.clear(); context = next }
}
export function resetDiaryColours(): void { seen.clear(); context = '' }

// A separate selected outline and today's ring leave the underlying day state readable.
// Only a change while this surface stays open animates, never opening, filtering or paging.
export function colourDay(node: HTMLElement, visits: readonly Clean[], key: string, today = false, facts: DayStateOptions = {}): HTMLElement {
  const value = dayState(visits, facts), before = seen.get(key)
  seen.set(key, value)
  node.dataset.dayState = value.state
  node.classList.toggle('diary-is-today', today)
  const label = value.state === 'open' && value.total && value.toCheck ? 'To check' : DAY_STATE_LABELS[value.state]
  const figure = dayFigure(visits, facts), shown = figure.shown, word = figure.counting
  const badge = el('span', { class: 'diary-day-state', role: 'img', 'data-state': value.state,
    'aria-label': `${label}, ${shown} of ${value.total} ${word}${value.closed ? ', day closed' : ''}${value.toCheck ? ', to check' : ''}`, title: `${label} · ${shown} of ${value.total} ${word}${value.closed ? ' · day closed' : ''}${value.toCheck ? ' · to check' : ''}` },
    ...(value.state === 'open' && value.total ? [] : [stateMark(value.state)]), ...(value.total ? [el('span', { class:'diary-state-fraction', 'aria-hidden': 'true' }, `${shown}/${value.total}`)] : []))
  if (value.toCheck) badge.append(el('span', {class:'diary-state-warning', role:'img', 'aria-label':'To check', title:'To check'}))
  const visible = document.visibilityState !== 'hidden'
  if (visible && before && before.state !== value.state) {
    node.style.cssText = `${node.style.cssText || ''};--diary-from-fill:var(--day-${before.state}-fill);--diary-from-line:var(--day-${before.state}-line)`
    node.classList.add('diary-colour-change')
  }
  if (visible && before && value.done > before.done && value.state === 'done') badge.querySelector('.diary-state-mark')?.classList.add('diary-state-tick-in')
  return badge
}

type KeyMemory = { days: number }
const counts = new Map<string, number>(), read = new Set<string>(), used = new Set<string>()
const visibility = new Map<string, boolean>(), retryAfter = new Map<string, number>()
const KEY_BACKOFF_MS = 60_000
let keySequence = 0
const who = (): string | null => { const m = getMe(); return m?.role === 'owner' && m.email && m.businessId ? `${m.businessId}:${m.email}` : null }
const mayRequest = (identity: string): boolean => navigator.onLine !== false && Date.now() >= (retryAfter.get(identity) ?? 0)
const failed = (identity: string): void => { retryAfter.set(identity, Date.now() + KEY_BACKOFF_MS) }
function syncKey(box: HTMLElement, identity: string): void {
  const shown = visibility.get(identity) ?? (counts.get(identity) ?? 0) < 28
  const line = box.querySelector<HTMLElement>('.diary-colour-key-line'), link = box.querySelector<HTMLElement>('.diary-colour-key-link')
  if (line) line.hidden = !shown
  box.classList.toggle('is-folded', !shown)
  if (link) {
    link.hidden = false
    link.replaceChildren(...(shown ? ['Hide key'] : [el('span', {class:'diary-key-help', 'aria-hidden':'true'}, '?'), 'What do the colours mean?']))
    link.setAttribute('aria-expanded', String(shown))
  }
}
function syncKeys(identity: string): void {
  for (const box of document.querySelectorAll<HTMLElement>('.diary-colour-key')) if (box.dataset.colourOwner === identity) syncKey(box, identity)
}
function saveCount(identity: string, days: number): void {
  counts.set(identity, Math.max(counts.get(identity) ?? 0, days))
  if (who() === identity) syncKeys(identity)
}
export function diaryColourKey(month = false, sign = '£'): HTMLElement {
  const identity = who(), memory = identity ?? 'unsigned'
  const box = el('div', { class: 'diary-colour-key', 'data-colour-owner':memory })
  const lineId = `diary-colour-key-${++keySequence}`
  const line = el('div', { class: 'diary-colour-key-line', id:lineId, role:'group', tabindex:'0', 'aria-label':'Diary colours' })
  for (const state of ['done', 'agreed', 'pencilled', 'open'] as const) line.append(el('span', {}, el('i', { class: 'diary-key-swatch', 'data-day-state': state }, stateMark(state)), DAY_STATE_LABELS[state]))
  line.append(el('span', {}, el('i', { class: 'diary-key-swatch diary-is-today' }), 'Today'))
  if (month) line.append(
    el('span', {}, el('i', { class: 'key-dot', 'aria-hidden': 'true' }), 'Suggested by Roundly'),
    el('span', {}, el('b', { class: 'key-paid', 'aria-hidden': 'true' }, '✓'), 'Done · paid'),
    el('span', {}, el('b', { class: 'key-owed', 'aria-hidden': 'true' }, sign), 'Done · not paid'))
  line.addEventListener('keydown', e => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
    e.preventDefault()
    line.scrollLeft = e.key === 'Home' ? 0 : e.key === 'End' ? line.scrollWidth : line.scrollLeft + (e.key === 'ArrowLeft' ? -120 : 120)
  })
  const link = el('button', { type:'button', class:'diary-colour-key-link', 'aria-controls':lineId, 'aria-expanded':'false' }, 'What do the colours mean?')
  link.addEventListener('click', () => {
    visibility.set(memory, line.hidden)
    syncKeys(memory); syncKey(box, memory)
    if (!line.hidden) line.focus({preventScroll:true}); else link.focus({preventScroll:true})
  })
  box.append(line, link); syncKey(box, memory)
  if (identity && !read.has(identity) && mayRequest(identity)) {
    read.add(identity)
    // Failed attempts remain recorded for this session. Renders never retry them in a loop.
    void api<KeyMemory>('/api/me/diary-colours').then(value => { saveCount(identity, value.days); if (who() === identity) syncKey(box, memory) }).catch(() => failed(identity))
  }
  return box
}
// One attempt per owner/server-day in this session. Offline skips rather than queuing a learned day.
// The shared back-off also prevents another learning request immediately after a failed read.
export function usedDiaryWeek(): void {
  const identity = who(), day = todayISO(), key = `${identity}:${day}`
  if (!identity || used.has(key) || !mayRequest(identity)) return
  used.add(key)
  void postNow<KeyMemory>('/api/me/diary-colours', {}).then(value => saveCount(identity, value.days)).catch(() => failed(identity))
}
